import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { TravelBooking } from '../queries';
import { confirmedOptionalItemRows } from '../confirmedOptionalItems';
import { formatMoney } from '../money';

const LEG_LABEL_KEY = {
  outbound: 'travelServices.booking.outbound',
  return: 'travelServices.booking.return',
} as const;

export function ConfirmedOptionalItems({
  booking,
}: {
  booking: Pick<TravelBooking, 'confirmedOptionalItems' | 'selectedOptions'>;
}) {
  const { t } = useTranslation();
  const rows = confirmedOptionalItemRows({
    confirmed: booking.confirmedOptionalItems,
    selected: booking.selectedOptions,
  });
  if (!rows.length) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('travelServices.booking.confirmedOptionalItems.title')}
      </Typography>
      <Stack spacing={0.75}>
        {rows.map((item, index) => (
          <Box
            key={`${item.detailBookingCode ?? item.name}-${item.direction ?? ''}-${index}`}
            display="flex"
            alignItems="center"
            gap={1}
            px={1.5}
            py={0.75}
            bgcolor="grey.100"
            borderRadius={1}
            flexWrap="wrap"
          >
            <Box flex={1} minWidth={180}>
              <Typography variant="body2">
                {item.quantity > 1 ? `${item.quantity} × ` : ''}
                {item.name}
              </Typography>
              {item.direction && (
                <Typography variant="caption" color="text.secondary">
                  {t(LEG_LABEL_KEY[item.direction])}
                </Typography>
              )}
            </Box>
            {item.providerAdded && (
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={t('travelServices.booking.confirmedOptionalItems.providerAdded')}
              />
            )}
            {item.free ? (
              <Typography variant="body2" color="success.main">
                {t('travelServices.booking.confirmedOptionalItems.free')}
              </Typography>
            ) : item.price ? (
              <Typography variant="body2">{formatMoney(item.price)}</Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
